import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Loader2, ExternalLink, FileText } from 'lucide-react';
import { BlogAPI, resolveImage } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import ImageUpload from './ImageUpload';
import RichTextEditor from './RichTextEditor';

const EMPTY = { title: '', slug: '', summary: '', body_html: '', cover_image: '', author: '', published: true };

const BlogTab = () => {
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => { setLoading(true); try { setPosts(await BlogAPI.adminList()); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing('new'); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditing(p); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.title.trim()) { toast({ title: 'Titre requis' }); return; }
    setBusy(true);
    try {
      if (editing === 'new') await BlogAPI.create(form);
      else await BlogAPI.update(editing.id, form);
      toast({ title: '✅ Article sauvegardé' });
      close();
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde échouée' });
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer "${p.title}" ?`)) return;
    await BlogAPI.remove(p.id);
    toast({ title: 'Article supprimé' });
    load();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="blog-new">
          <Plus className="w-4 h-4 mr-1" /> Nouvel article
        </Button>
      </div>
      <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#7A6F5C]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement...</div>
        ) : posts.length === 0 ? (
          <div className="p-10 text-center text-[#7A6F5C]"><FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />Aucun article publié.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] text-left text-[#7A6F5C] text-xs uppercase tracking-wider">
              <tr><th className="px-4 py-3">Titre</th><th className="px-4 py-3">Slug</th><th className="px-4 py-3">Auteur</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3 text-right"></th></tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-t border-[#F5F1EA]" data-testid={`blog-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium text-[#2D2A26]">
                    <div className="flex items-center gap-2">
                      {p.cover_image && <img src={resolveImage(p.cover_image)} alt="" className="w-10 h-10 rounded object-cover" onError={e => { e.target.style.visibility = 'hidden'; }} />}
                      {p.title}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#5A5247] font-mono text-xs">{p.slug}</td>
                  <td className="px-4 py-3 text-[#5A5247]">{p.author || '—'}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${p.published ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}>{p.published ? 'Publié' : 'Brouillon'}</span></td>
                  <td className="px-4 py-3 text-right">
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-[#F5F1EA] rounded text-[#1F4D3F] inline-block mr-1"><ExternalLink className="w-4 h-4" /></a>
                    <button onClick={() => openEdit(p)} className="p-1.5 hover:bg-[#F5F1EA] rounded text-[#1F4D3F]" data-testid={`blog-edit-${p.id}`}><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(p)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600 ml-1" data-testid={`blog-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editing === 'new' ? 'Nouvel article' : 'Modifier l\'article'}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Titre *</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-form-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Slug URL (optionnel — auto)</label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-genere-depuis-titre" />
              </div>
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Auteur</label>
                <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="Dr ..." />
              </div>
            </div>
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Résumé (carte d'article)</label>
              <Textarea rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Court résumé affiché sur la liste" />
            </div>
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Contenu de l'article *</label>
              <RichTextEditor value={form.body_html} onChange={(html) => setForm({ ...form, body_html: html })} placeholder="Tapez votre article…" testId="blog-form-body" minHeight={280} />
            </div>
            <ImageUpload value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} label="Image de couverture" testId="blog-form-cover" />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 rounded text-[#1F4D3F]" /> Publié
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={close}>Annuler</Button>
            <Button onClick={save} disabled={busy} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="blog-form-save">{busy ? 'Sauvegarde...' : 'Enregistrer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogTab;
